import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bot, Shield, BarChart3, Activity, Sparkles, CreditCard, Plug } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { useAuth, hasPermission } from "@/lib/auth";
import { PageShell, PageStat } from "@/components/page-shell";
import type { Chatbot, Conversation } from "@shared/schema";

export default function OrgAdmin() {
  const { user, organization } = useAuth();
  // Same gate as the /integrations route in App.tsx: plan feature +
  // integrations.manage permission (super_admin bypasses inside helpers).
  const showIntegrations =
    (user?.role === "super_admin" || ((organization as any)?.features ?? []).includes("integrations")) &&
    hasPermission(user, "integrations.manage");
  const { data: chatbots, isLoading: botsLoading } = useQuery<Chatbot[]>({ queryKey: ["/api/chatbots"] });
  const { data: conversations } = useQuery<Conversation[]>({
    queryKey: ["/api/chatbots", "org-conversations"],
  });
  const { data: aiIntegration } = useQuery<{ configured: boolean; hasKey: boolean; providerName?: string | null; model?: string | null }>({
    queryKey: ["/api/organization/ai-integration"],
  });
  const aiConfigured = aiIntegration?.hasKey ?? !!organization?.openaiApiKey;

  const [selectedBot, setSelectedBot] = useState<string | null>(null);

  const activeBots = chatbots?.filter(b => b.status === "active") || [];
  const totalBots = chatbots?.length || 0;

  if (botsLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  return (
    <PageShell
      eyebrow={`${organization?.plan ?? ""} plan`}
      titleTestId="text-org-admin-title"
      title={
        <span className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          Organization Admin
        </span>
      }
      subtitle={`Manage ${organization?.name || "your organization"}.`}
      stats={
        <>
          <PageStat label="Chatbots" value={totalBots} hint={`${activeBots.length} active`} icon={<Bot className="w-4 h-4" />} />
          <PageStat label="Messages used" value={organization?.messagesUsed ?? 0} icon={<Activity className="w-4 h-4" />} />
          <PageStat label="Monthly limit" value={organization?.monthlyMessageLimit?.toLocaleString() ?? "—"} icon={<BarChart3 className="w-4 h-4" />} />
          <PageStat label="Agent limit" value={organization?.agentLimit ?? 2} icon={<Bot className="w-4 h-4" />} />
        </>
      }
    >
      {/* Legacy test-ids kept off-screen so old tests/scripts don't break. */}
      <div className="hidden">
        <span data-testid="text-org-bots">{totalBots}</span>
        <span data-testid="text-org-usage">{organization?.messagesUsed ?? 0}</span>
        <span data-testid="text-org-limit">{organization?.monthlyMessageLimit?.toLocaleString()}</span>
        <span data-testid="text-org-agent-limit">{organization?.agentLimit ?? 2}</span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Link href="/chatbots" className="block">
          <div className="feature-tile h-full" data-testid="quick-action-org-chatbots">
            <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(160 70% 45%), hsl(170 70% 40%))" }}>
              <Bot className="w-4 h-4" />
            </div>
            <div className="font-medium text-sm">Chatbots</div>
            <p className="text-xs text-muted-foreground mt-1">Configure your assistants and knowledge.</p>
          </div>
        </Link>
        {showIntegrations && <Link href="/integrations" className="block">
          <div className="feature-tile h-full" data-testid="quick-action-org-integrations">
            <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(262 70% 60%), hsl(280 70% 55%))" }}>
              <Plug className="w-4 h-4" />
            </div>
            <div className="font-medium text-sm">Integrations</div>
            <p className="text-xs text-muted-foreground mt-1">Slack, Teams, ServiceNow, custom APIs.</p>
          </div>
        </Link>}
        <Link href="/settings" className="block">
          <div className="feature-tile h-full" data-testid="quick-action-org-billing">
            <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(28 92% 48%), hsl(18 92% 42%))" }}>
              <CreditCard className="w-4 h-4" />
            </div>
            <div className="font-medium text-sm">Plan & billing</div>
            <p className="text-xs text-muted-foreground mt-1">Upgrade, see usage, manage payment.</p>
          </div>
        </Link>
        <Link href="/settings" className="block">
          <div className="feature-tile h-full" data-testid="quick-action-org-branding">
            <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(340 80% 60%), hsl(320 75% 55%))" }}>
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="font-medium text-sm">Branding & members</div>
            <p className="text-xs text-muted-foreground mt-1">Logo, colours, invite teammates.</p>
          </div>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Organization Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-md bg-muted/50">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider font-medium">Name</p>
              <p className="text-sm font-medium mt-1 break-words">{organization?.name}</p>
            </div>
            <div className="p-3 rounded-md bg-muted/50">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider font-medium">Slug</p>
              <p className="text-sm font-medium mt-1 break-words">/{organization?.slug}</p>
            </div>
            <div className="p-3 rounded-md bg-muted/50">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider font-medium">Plan</p>
              <Badge variant="default" className="mt-1">{organization?.plan}</Badge>
            </div>
            <div className="p-3 rounded-md bg-muted/50">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider font-medium">Platform AI Key</p>
              <Badge variant={aiConfigured ? "default" : "destructive"} className="mt-1" data-testid="status-platform-ai-key">
                {aiConfigured ? "Configured" : "Not Set"}
              </Badge>
              {aiConfigured && (aiIntegration?.providerName || aiIntegration?.model) && (
                <p className="text-[11px] text-muted-foreground mt-1" data-testid="text-platform-ai-detail">
                  {[aiIntegration?.providerName, aiIntegration?.model].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="bots">
        <TabsList>
          <TabsTrigger value="bots" data-testid="tab-org-bots">
            <Bot className="w-4 h-4 mr-1" />My Bots ({totalBots})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bots" className="space-y-3 mt-4">
          {chatbots && chatbots.length > 0 ? (
            chatbots.map((bot) => (
              <Card key={bot.id} data-testid={`card-bot-${bot.id}`}>
                <CardContent className="p-4">
                  {/* 320px reflow: badges wrap to their own row; name/model
                      break instead of squeezing into 1-char columns. */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1 basis-[12rem]">
                      <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bot.primaryColor || "#0c3547" }}>
                        <Bot className="w-5 h-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium break-words sm:truncate">{bot.name}</p>
                        <p className="text-xs text-muted-foreground capitalize break-words">{bot.type} chatbot | Model: {bot.model}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={bot.status === "active" ? "default" : "secondary"}>{bot.status}</Badge>
                      <Badge variant="outline" className="text-[10px]">{bot.theme}</Badge>
                    </div>
                  </div>
                  {bot.scopeDescription && (
                    <p className="text-xs text-muted-foreground mt-2 break-words sm:pl-[52px]">Scope: {bot.scopeDescription}</p>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="text-center py-8">
              <Bot className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">No chatbots yet. Create one from the Chatbots page.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
